import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { INFORMATION_PDFS, fileNameOf, openStoredPdf, toHref } from "./informationFiles";
import ProductLocationOverrides from "./ProductLocationOverrides";

/**
 * Sales > Utilities > Products -- the Product Information tab.
 *
 * The three things a customer may want to look at while approving an order:
 * the product's page on the supplier's site, the product sheet PDF and the
 * size chart PDF. The approval card reads them back as Link / Image / Size
 * Chart buttons (CustomerWoApproval), matched on styleId.
 *
 * Two cards, 50/50 (user, 2026-10-16), laid out by ProductLocationOverrides:
 *   - DEFAULT (left): the three columns on the product row. While a location
 *     is being added or edited, its form takes this place instead.
 *   - LOCATION OVERRIDES (right): per-location values for only the locations
 *     this product needs them for.
 * The approval card takes each field from the work order's location when set,
 * else the Default, else shows no button (nova-api utils/productInformation.js).
 *
 * The PDFs store an S3 KEY, never a signed URL: a URL carries an expiry and
 * would stop working. The key is turned into a link at the moment of opening.
 */

export { INFORMATION_PDFS, fileNameOf, toHref, openStoredPdf } from "./informationFiles";

export default function SProductInformation({
  formData,
  setFormData,
  isUpdateMode,
  onSaveProduct,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);

  const [busyField, setBusyField] = useState(null);
  const [opening, setOpening] = useState(false);

  const notify = (message, type) =>
    dispatch(setNotification({ message, type }));
  const errorText = (err) => err?.response?.data?.message || err.message;

  const folderFor = () =>
    `kms/sales/product/${user?.companyId}/${formData?.productId}/information`;

  const openStoredFile = async (key) => {
    if (!key) return;
    try {
      setOpening(true);
      await openStoredPdf(key);
    } catch (err) {
      notify(errorText(err), "error");
    } finally {
      setOpening(false);
    }
  };

  const uploadFile = async (field, file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      notify("That is not a PDF.", "error");
      return;
    }

    const body = new FormData();
    body.append("file", file);
    body.append("folder", folderFor());
    body.append("id", formData?.id);
    body.append("field", field);

    try {
      setBusyField(field);
      const { data } = await API.patch("/sales/product/information-file", body, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      if (!data?.success) throw new Error(data?.message || "Upload failed");
      // The server decides the stored key, so take it from the response rather
      // than rebuilding it here and risking the two drifting apart.
      setFormData((prev) => ({ ...prev, [field]: data.data?.[field] }));
      notify("Uploaded", "success");
    } catch (err) {
      notify(errorText(err), "error");
    } finally {
      setBusyField(null);
    }
  };

  const removeFile = async (field) => {
    try {
      setBusyField(field);
      const { data } = await API.post(
        "/sales/product/information-file/delete",
        { id: formData?.id, field },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      setFormData((prev) => ({ ...prev, [field]: null }));
      notify("Removed", "success");
    } catch (err) {
      notify(errorText(err), "error");
    } finally {
      setBusyField(null);
    }
  };

  return (
    <div className="row g-3 align-items-stretch">
      {opening && <Spinner />}

      {/*   Default -- handed to the overrides component, which shows it on
            the left unless a location is being added or edited there.
       ********************************************* */}
      <ProductLocationOverrides
        formData={formData}
        isUpdateMode={isUpdateMode}
        defaultCard={
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-secondary bg-opacity-25 border-0 py-1">
              <h5 className="text-secondary mb-0">
                <i className="mdi mdi-information-outline mr-1"></i> Default
              </h5>
            </div>
            <div className="card-body p-3">
              <p className="text-muted small mb-3">
                Used on the approval card wherever a location has no override.
              </p>
  
              <div className="mb-3">
                <label htmlFor="product-url" className="form-label fw-bold mb-1">
                  Product Url
                </label>
                {/* No align-items: the icon button stretches to the input's height. */}
                <div className="d-flex">
                  <input
                    id="product-url"
                    type="text"
                    value={formData?.productUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        productUrl: e.target.value,
                      }))
                    }
                    className="form-control"
                    placeholder="Enter Product Url"
                  />
                  <a
                    href={toHref(formData?.productUrl) || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn btn-${
                      formData?.productUrl ? "info" : "secondary disabled"
                    } rounded d-flex align-items-center px-2 ms-1`}
                    aria-disabled={!formData?.productUrl}
                    aria-label="Open Product Url"
                    title="Open"
                  >
                    <i className="mdi mdi-open-in-new fs-4"></i>
                  </a>
                </div>
                <small className="text-muted">
                  The product's page on the supplier's site.
                </small>
              </div>
  
              {INFORMATION_PDFS.map(({ field, label, hint }) => {
                const stored = formData?.[field];
                const busy = busyField === field;
  
                return (
                  <div className="mb-3" key={field}>
                    <label className="form-label fw-bold mb-1" htmlFor={field}>
                      {label}
                    </label>
  
                    {!isUpdateMode ? (
                      <div className="text-muted small">
                        Save the product first to attach a PDF.
                      </div>
                    ) : (
                      // Same row as the customer's Tax Exemption Certificate
                      // (user, 2026-10-16): the file box fills the width, the
                      // download and delete buttons sit on the right, grey and
                      // disabled until there is a file. To replace a PDF,
                      // delete it and pick the new one.
                      <div className="d-flex">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          {busy ? (
                            <div className="form-control text-muted">
                              <i className="mdi mdi-loading mdi-spin"></i>{" "}
                              working…
                            </div>
                          ) : stored ? (
                            <div className="form-control text-truncate" title={fileNameOf(stored)}>
                              <i className="mdi mdi-file-pdf-box text-danger"></i>{" "}
                              {fileNameOf(stored)}
                            </div>
                          ) : (
                            <input
                              id={field}
                              type="file"
                              accept="application/pdf"
                              className="form-control"
                              onChange={(e) => {
                                uploadFile(field, e.target.files?.[0]);
                                // Cleared so picking the SAME file again still
                                // fires onChange.
                                e.target.value = "";
                              }}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          aria-label={`View ${label}`}
                          title="View"
                          disabled={!stored || busy}
                          onClick={() => openStoredFile(stored)}
                          className={`btn btn-sm btn-${
                            stored ? "success" : "secondary"
                          } rounded py-0 px-1 ms-1`}
                        >
                          <i className="mdi mdi-arrow-down-bold fs-3"></i>
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${label}`}
                          title="Remove"
                          disabled={!stored || busy}
                          onClick={() => {
                            if (window.confirm(`Remove the ${label}?`)) removeFile(field);
                          }}
                          className={`btn btn-${
                            stored ? "danger" : "secondary"
                          } rounded ms-1`}
                        >
                          <i className="mdi mdi-delete"></i>
                        </button>
                      </div>
                    )}
                    <small className="text-muted d-block">{hint}</small>
                  </div>
                );
              })}
  
              {/* The PDFs save themselves as they are uploaded; the Url is an
                  ordinary field on the product, so it needs the product saved.
                  In add mode it goes over with the rest of the form. */}
              {isUpdateMode && (
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveProduct}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}
